import React from "react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider as _QueryClientProvider,
} from "@tanstack/react-query";
import { useLogout } from "./hooks/useLogout";
import toast from "react-hot-toast";
import { ResponseError } from "@norma-bi/bi-api";
import { throttle } from "@norma-bi/utils";
import { useCookies } from "react-cookie";

const MAX_RETRIES = 3;

export function QueryClientProvider(
  props: React.PropsWithChildren<{
    retries?: boolean;
    /**
     * When true skipTokenCheck disables the automatic logout mechanism that triggers when the token cookie is missing.
     * Primarily used in test environments to prevent unexpected logouts during tests
     * In the normal flow, this should always be false
     */
    skipTokenCheck?: boolean;
  }>,
) {
  const { retries = true, skipTokenCheck = false } = props;
  const [cookies] = useCookies(["token"]);

  const [mustLogout, setMustLogout] = React.useState(false);

  const showUnknownErrorToast = React.useMemo(
    () =>
      throttle(() => {
        toast.error("Something went wrong, please try again later");
      }, 1000),
    [],
  );

  const onError = React.useCallback(
    (err: Error) => {
      showUnknownErrorToast();

      if (err instanceof ResponseError) {
        console.error("Response error: ", err.response);
      }
    },
    [showUnknownErrorToast],
  );

  const queryClient = React.useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          retry: (numRetries, err) => {
            const respErr = err as Partial<{ response: { status: number } }>;
            if (respErr?.response?.status === 401) {
              setMustLogout(true);
              return false;
            }
            if (respErr?.response?.status === 403) {
              return false;
            }
            return retries && numRetries < MAX_RETRIES;
          },
        },
      },
      queryCache: new QueryCache({
        onError: (err, query) => {
          if (query.meta?.preventDefaultErrorHandling) {
            return;
          }
          return onError(err);
        },
      }),
      mutationCache: new MutationCache({
        onError: (err, _vars, _ctx, mutation) => {
          if (mutation.meta?.preventDefaultErrorHandling) {
            return;
          }
          return onError(err);
        },
      }),
    });
  }, [onError, retries]);

  React.useEffect(() => {
    if (skipTokenCheck) return;

    const handleVisibilityChange = () => {
      if (!document.hidden && !cookies.token) {
        setMustLogout(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [skipTokenCheck, cookies.token]);

  return (
    <_QueryClientProvider client={queryClient}>
      <LogoutHandler logout={mustLogout}>{props.children}</LogoutHandler>
    </_QueryClientProvider>
  );
}

function LogoutHandler(props: React.PropsWithChildren<{ logout: boolean }>) {
  const { logout } = useLogout();

  React.useEffect(() => {
    if (props.logout) {
      logout();
    }
  }, [props.logout, logout]);

  return props.children;
}
